import {
  Game,
  getBalltrackerByMachineUuidUnifiedOptions,
  getMachinesSearchOptions,
  GetMachinesSearchResponse,
  getMachinesSearchTaggingOptions,
  getScheduleByScheduleUuidOptions,
  MachinePublicHydrated,
  postGameByGameIdEditTimesMutation,
  postGameByGameIdEndMutation,
} from "@aura-frontend/omnibus-az-client";
import {
  QueryClient,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import {
  Button,
  Dialog,
  DialogFooter,
  DialogHeader,
  DialogScrollableContent,
  DialogTitle,
  toast,
} from "@win-reality/react-ui";
import { useRef, useState } from "react";
import { Navigate, useBlocker, useNavigate, useParams } from "react-router-dom";

import { EventForm } from "@/components/EventForm/EventForm";
import { EventFormData } from "@/components/EventForm/EventForm.schema";
import BackButton from "@/components/layout/BackButton";
import { PageSpinner } from "@/components/ui/PageSpinner/PageSpinner";
import { useField } from "@/hooks/useComplexes";
import { ROUTES } from "@/lib/routes";
import { getHomeAwayLineups } from "@/pages/Tagging/utils/api-helpers";
import { formatYearMonth, parseTimeToUnixSeconds } from "@/utils/dateUtils";
import { resolveLineupToSave } from "@/utils/lineupUtils";
import {
  getLiveScheduleGameUuid,
  hasScheduleStarted,
} from "@/utils/scheduleUtils";

import { SCHEDULES_QUERY_KEY } from "../Complexes/SingleField/FieldSchedule/FieldSchedule.queries";
import { useUpdateEvent } from "./EditEvent.queries";

// Patches `currentGame` for `machineUuid` across the caches that hold it. By
// default updates both the `/balltracker/{uuid}/unified` cache (used by the
// EditEvent page itself) and the two `/machines/search*` listings (used by
// FieldCard). Pass `{ skipUnified: true }` when the caller is about to
// navigate away — patching `/unified` would re-render EditEvent with
// `currentGame=null`, flip `isCurrentGame` to false, and trigger the schedule
// query to fire a 404 against the gameUuid before navigation completes.
function patchMachineCurrentGame(
  queryClient: QueryClient,
  machineUuid: string,
  mutate: (current: Game | null | undefined) => Game | null,
  options: { skipUnified?: boolean } = {},
): void {
  if (!options.skipUnified) {
    const unifiedKey = getBalltrackerByMachineUuidUnifiedOptions({
      path: { machine_uuid: machineUuid },
    }).queryKey;
    queryClient.setQueryData<unknown>(unifiedKey, (cached: unknown) => {
      if (!cached || typeof cached !== "object" || !("body" in cached))
        return cached;
      const body = (cached as { body?: unknown }).body;
      if (!body || typeof body !== "object") return cached;
      const machine = (body as { machine?: MachinePublicHydrated | null })
        .machine;
      if (!machine) return cached;
      return {
        ...(cached as object),
        body: {
          ...(body as object),
          machine: { ...machine, currentGame: mutate(machine.currentGame) },
        },
      };
    });
  }

  for (const opts of [
    getMachinesSearchOptions({}),
    getMachinesSearchTaggingOptions({}),
  ]) {
    const key = opts.queryKey;
    queryClient.setQueryData<GetMachinesSearchResponse>(key, (cached) => {
      if (!cached) return cached;
      return {
        ...cached,
        machines: cached.machines.map((m) =>
          m.machineUuid === machineUuid
            ? { ...m, currentGame: mutate(m.currentGame) }
            : m,
        ),
      };
    });
  }
}

function EditEvent() {
  const { complexName, fieldId, eventId } = useParams<{
    complexName: string;
    fieldId: string;
    eventId: string;
  }>();

  if (!complexName || !fieldId || !eventId) {
    throw new Error(
      "Missing complexName, fieldId or eventId in URL parameters",
    );
  }

  const navigate = useNavigate();
  const [isFormDirty, setIsFormDirty] = useState(false);
  const skipBlocker = useRef(false);
  const blocker = useBlocker(() => isFormDirty && !skipBlocker.current);
  const { field, isLoadingField } = useField(fieldId);

  const currentGameUuid = field?.currentGame?.gameUuid;
  const isCurrentGame = !!currentGameUuid && currentGameUuid === eventId;

  const { data: schedule, isLoading: isLoadingSchedule } = useQuery({
    ...getScheduleByScheduleUuidOptions({
      path: { scheduleUuid: eventId },
    }),
    enabled: !isLoadingField && !!field && !isCurrentGame,
  });

  if (!isLoadingField && !field) {
    throw new Error("Field not found");
  }

  const { updateEvent, isUpdatingEvent } = useUpdateEvent(eventId);

  const handleSubmit = async (data: EventFormData) => {
    if (!field) {
      throw new Error("Field data is required to update an event");
    }

    const { overview, configuration, lineup, gameTeams } = data;
    const isGame = overview.type === "game";

    // Saving replaces the stored lineups, so untouched ones go back as stored
    const savedGame = schedule?.merged?.game;
    const savedLineups =
      savedGame?.type === "game"
        ? getHomeAwayLineups(savedGame)
        : { home: [], away: [] };
    const lineupToSave = (side: "home" | "away") =>
      isGame
        ? resolveLineupToSave({
            saved: savedLineups[side],
            current:
              lineup?.[side === "home" ? "homeTeam" : "awayTeam"] ?? null,
            isSameTeam:
              (savedGame?.teams?.[side] ?? "") ===
              (gameTeams?.[side].uuid ?? ""),
          })
        : [];
    // Older games stored no team names; their name stays as it was rather
    // than becoming "Home x Away"
    const hasTeamNames = !!gameTeams?.home.name && !!gameTeams?.away.name;

    if (!overview.startDate) {
      throw new Error("Start date is required to update an event");
    }
    const startDate = overview.startDate;

    const yearMonth = formatYearMonth(startDate);
    const scheduleTimeS = parseTimeToUnixSeconds(startDate, overview.startTime);
    const endTimeS = parseTimeToUnixSeconds(startDate, overview.endTime);

    try {
      await updateEvent({
        body: {
          id: eventId,
          machine_uuid: field.machineUuid,
          year_month: yearMonth,
          schedule_time_s: scheduleTimeS,
          internal: {
            name: overview.name,
            notes: overview.additionalNotes,
            led: configuration.ledBoardMode,
            // Omnibus replaces the whole internal record, so an org left out
            // here is cleared from the event
            ...(overview.orgId && { org_id: overview.orgId }),
            game: {
              type: overview.type,
              generated_display_name:
                overview.type === "game"
                  ? !hasTeamNames && savedGame?.displayName
                    ? savedGame.displayName
                    : `${gameTeams?.home.name || "Home"} x ${gameTeams?.away.name || "Away"}`
                  : overview.teamName || overview.name || "Practice",
              machine_uuid: field.machineUuid,
              lineups: {
                homeLineup: lineupToSave("home"),
                awayLineup: lineupToSave("away"),
              },
              sport: configuration.sport,
              teams: {
                home:
                  overview.type === "game"
                    ? (gameTeams?.home.uuid ?? "")
                    : (overview.team ?? ""),
                away: isGame ? (gameTeams?.away.uuid ?? "") : "",
              },
              teamNames: {
                home:
                  overview.type === "game"
                    ? (gameTeams?.home.name ?? "")
                    : (overview.teamName ?? ""),
                away: isGame ? (gameTeams?.away.name ?? "") : "",
              },
              ...(data.teamColors && { teamColors: data.teamColors }),
              times: [
                {
                  startTime: scheduleTimeS,
                  endTime: endTimeS,
                  machineUuid: field.machineUuid,
                },
              ],
              year_month: yearMonth,
            },
            park: {
              sport: configuration.sport,
              rubberDistanceF: configuration.moundDistance,
              ...(configuration.strikeZone && {
                strikeZone: configuration.strikeZone,
              }),
              field: {
                ageGroup: configuration.level,
                name: field.name,
                diamondSizeFeet: configuration.baseDistance ?? 0,
                infieldAtFoulLineFeet: configuration.infieldAtFoulLine ?? 0,
                fenceFirstBaseFeet: configuration.fenceFirstBase ?? 0,
                fenceThirdBaseFeet: configuration.fenceThirdBase ?? 0,
                fenceLeftFieldFeet: configuration.leftField ?? 0,
                fenceCenterFieldFeet: configuration.centerField ?? 0,
                fenceRightFieldFeet: configuration.rightField ?? 0,
              },
            },
          },
        },
      });
      skipBlocker.current = true;
      toast("Event updated successfully", { variant: "success" });
      navigate(
        ROUTES.MANAGE_FIELD.replace(":complexName", complexName).replace(
          ":fieldId",
          fieldId,
        ),
      );
    } catch {
      toast("Failed to update event. Please try again.", { variant: "error" });
    }
  };

  const queryClient = useQueryClient();
  const { mutateAsync: editGameTimes } = useMutation({
    ...postGameByGameIdEditTimesMutation(),
    onSuccess: async () => {
      // The /machines/search caches are updated directly by the handler via
      // patchMachineCurrentGame; no invalidation here so the patch isn't
      // immediately overwritten by a refetch.
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: [SCHEDULES_QUERY_KEY],
          exact: false,
          refetchType: "all",
        }),
        queryClient.invalidateQueries({
          queryKey: getScheduleByScheduleUuidOptions({
            path: { scheduleUuid: eventId },
          }).queryKey,
        }),
      ]);
    },
  });

  const { mutateAsync: endGame } = useMutation({
    ...postGameByGameIdEndMutation(),
    onSuccess: async () => {
      // The /machines/search caches are updated directly by the handler via
      // patchMachineCurrentGame; no invalidation here so the patch isn't
      // immediately overwritten by a refetch.
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: [SCHEDULES_QUERY_KEY],
          exact: false,
          refetchType: "all",
        }),
        queryClient.invalidateQueries({
          queryKey: getScheduleByScheduleUuidOptions({
            path: { scheduleUuid: eventId },
          }).queryKey,
        }),
      ]);
    },
  });

  const handleLiveEndTimeUpdate = async (endTime: string) => {
    const gameUuid = field?.currentGame?.gameUuid;
    if (!gameUuid) return;
    const currentGame = field.currentGame!;

    const startTimestamp = currentGame.times[0]?.startTime;

    const [hours, minutes] = endTime.split(":").map(Number);

    const endDate = new Date((startTimestamp ?? Date.now() / 1000) * 1000);
    endDate.setHours(hours, minutes, 0, 0);

    const endTimeS = Math.floor(endDate.getTime() / 1000);

    const machineUuid = field.machineUuid;

    try {
      await editGameTimes({
        path: { game_id: gameUuid },
        body: {
          times: [
            {
              startTime: startTimestamp ?? 0,
              endTime: endTimeS,
              machineUuid: machineUuid,
            },
          ],
        },
      });

      patchMachineCurrentGame(queryClient, machineUuid, (game) => {
        if (!game) return game ?? null;
        const nextTimes = [...(game.times ?? [])];
        nextTimes[0] = {
          ...nextTimes[0],
          startTime: startTimestamp ?? 0,
          endTime: endTimeS,
        };
        return { ...game, times: nextTimes };
      });

      toast("End time updated successfully", { variant: "success" });
    } catch {
      toast("Failed to update end time. Please try again.", {
        variant: "error",
      });
      throw new Error("Failed to update end time");
    }
  };

  const handleEndEvent = async () => {
    const gameUuid = field?.currentGame?.gameUuid;
    if (!gameUuid) return;

    try {
      await endGame({
        body: {},
        path: { game_id: gameUuid },
      });
      // Skip the /unified cache patch — setting currentGame=null on this page
      // would flip isCurrentGame to false, enable the schedule query, and 404
      // before navigate() completes. The destination page only reads from
      // /machines/search, which we still patch.
      patchMachineCurrentGame(queryClient, field.machineUuid, () => null, {
        skipUnified: true,
      });
      toast("Event ended successfully", { variant: "success" });
      skipBlocker.current = true;
      navigate(ROUTES.MANAGE_COMPLEX.replace(":complexName", complexName));
    } catch {
      toast("Failed to end event. Please try again.", { variant: "error" });
    }
  };

  const isLoading =
    isLoadingField || (!isCurrentGame && isLoadingSchedule && !!field);

  if (isLoading) {
    return <PageSpinner />;
  }

  // Omnibus ignores edits to a schedule once it has started, so it never
  // opens in the form: a live one shows its game's locked view (as the
  // field's active event card links), and any other goes back to the field
  if (schedule && hasScheduleStarted(schedule)) {
    const liveGameUuid = getLiveScheduleGameUuid(schedule, currentGameUuid);
    const to = liveGameUuid
      ? ROUTES.EDIT_EVENT.replace(":complexName", complexName)
          .replace(":fieldId", fieldId)
          .replace(":eventId", liveGameUuid)
      : ROUTES.MANAGE_FIELD.replace(":complexName", complexName).replace(
          ":fieldId",
          fieldId,
        );
    return <Navigate to={to} replace />;
  }

  return (
    <div className="min-h-full flex flex-col">
      <div className="sticky top-0 z-20 bg-white">
        <div className="pt-4 pb-4 max-w-130 mx-auto px-5 w-full grid grid-cols-[1fr_auto_1fr] items-center gap-2">
          <div className="flex justify-start">
            <BackButton />
          </div>
          <div className="text-center text-base">Event Details</div>
          <div />
        </div>
      </div>

      {field && (
        <EventForm
          complexName={complexName}
          fieldName={field.name}
          isFieldHealthy={field.status === "active"}
          fieldId={fieldId}
          onSubmit={handleSubmit}
          variant="edit"
          onDirtyChange={(dirty) => {
            setIsFormDirty(dirty);
            if (dirty) skipBlocker.current = false;
          }}
          isSubmitting={isUpdatingEvent}
          currentEvent={
            isCurrentGame ? (field.currentGame ?? undefined) : undefined
          }
          initialEventData={schedule}
          excludeScheduleId={eventId}
          onLiveEndTimeUpdate={
            isCurrentGame ? handleLiveEndTimeUpdate : undefined
          }
          onEndEvent={isCurrentGame ? handleEndEvent : undefined}
        />
      )}

      <Dialog
        open={blocker.state === "blocked"}
        onOpenChange={(open) => !open && blocker.reset?.()}
        defaultCancelButton={false}
        size="sm"
      >
        {({ showDivider, setScrollContainer }) => (
          <>
            <DialogScrollableContent setScrollContainer={setScrollContainer}>
              <DialogHeader>
                <DialogTitle>Are you sure?</DialogTitle>
              </DialogHeader>
              <p className="text-md ">
                You have unsaved changes to this event. If you leave now, your
                edits will be lost.
              </p>
            </DialogScrollableContent>
            <DialogFooter showDivider={showDivider}>
              <div className="flex w-full justify-between">
                <Button
                  variant="ghost"
                  className="underline"
                  onClick={() => blocker.reset?.()}
                >
                  Back
                </Button>
                <Button
                  variant="secondary-flat-light"
                  onClick={() => blocker.proceed?.()}
                >
                  Continue
                </Button>
              </div>
            </DialogFooter>
          </>
        )}
      </Dialog>
    </div>
  );
}

export { EditEvent };
